import '../../../sass/alpha-modules/optional/_image-gallery.scss'

import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'
import lightGallery from 'lightgallery'
import merge from 'lodash-es/merge'

export default class ImageGallery extends AlphaModule {
  constructor(...args) {
    super(...args)

    this._galleries = new Map()
  }

  getName() {
    return 'imageGallery'
  }

  getDefaultConfig() {
    const attrName = AlphaHelpers.getAlphaAttributeName('required-modules')

    return {
      galleriesSelector: `[${attrName}*="${this.getName()}"]`,
      useThumbnail: false,
      useZoom: false,
      useHash: false,
      lgConfig: {
        selector: '.image-gallery-link,.blocks-gallery-item a,.wp-block-image a,.wp-block-media-text figure a',
        download: false,
        licenseKey: 'your_license_key',
        plugins: []
      }
    }
  }

  _onLoad() {
    super._onLoad()

    this._loadGalleries()
  }

  _addGalleryInfo(el, gallery) {
    this._galleries.set(el, gallery)
  }

  _loadGalleries() {
    const config = this.getConfig()

    // fast check: use querySelector to check if we have any gallery to load (because querySelectorAll is slow)
    const galleries = AlphaHelpers.smartQuerySelectorAll(config.galleriesSelector)
    const loadGalleriesAsync = async () => {
      for (const containerEl of galleries) {
        // wait until each gallery is loaded, because they might load extra components (hash etc.)
        // eslint-disable-next-line no-await-in-loop
        await this.loadGallery(containerEl)
      }
    }

    loadGalleriesAsync()
  }

  async loadGallery(galleryEl) {
    // try to merge config with inline config
    let config = this.getConfig()
    const lgConfigAttrName = AlphaHelpers.getAlphaAttributeName('lightgallery-config')
    const galleryInnerEl = galleryEl.querySelector(`[${lgConfigAttrName}]`)
    if (galleryInnerEl) {
      const customConfig = AlphaHelpers.getParsedJsonAttribute(galleryInnerEl, lgConfigAttrName)
      if (customConfig) {
        config = merge(config, customConfig)
      }
    }

    // load plugins if necessary
    if (config.useThumbnail) {
      const lgThumbnail = await import(
        /* webpackChunkName: "lg-thumbnail" */
        '../../vendors/lightgallery/thumbnail')
      config.lgConfig.plugins.push(await lgThumbnail.default)
    }

    if (config.useZoom) {
      const lgZoom = await import(
        /* webpackChunkName: "lg-zoom" */
        '../../vendors/lightgallery/zoom')
      config.lgConfig.plugins.push(await lgZoom.default)
    }

    if (config.useHash) {
      const lgHash = await import(
        /* webpackChunkName: "lg-hash" */
        '../../vendors/lightgallery/hash')
      config.lgConfig.plugins.push(await lgHash.default)
    }

    // initialize gallery and store it
    const gallery = lightGallery(galleryEl, config.lgConfig)
    this._addGalleryInfo(galleryEl, gallery)

    // inform other components that this gallery is loaded
    this.dispatchEvent('gallery.loaded', {
      el: galleryEl,
      gallery
    })
  }

  getGalleryFromElement(el) {
    return this._galleries.get(el)
  }
}
